import { useEffect, useState } from "react";
import {
  HOMEPAGE_VERSION,
  fireHomepageGtm,
  isBannerActive,
  readBannerState,
  setHomepageBannerShowing,
  writeBannerState,
} from "../../utils/homepageVersion";

/**
 * Visibility and the close action for the homepage upgrade banner.
 *
 * The three-day window starts the first time the banner is mounted for this
 * student. Closing it hides it for the rest of this visit and counts the close;
 * it comes back on the next visit until it has been closed three times.
 *
 * Each mount is one visit to the homepage, so it also records which version the
 * student landed on — the denominator for the switch-back rate. The mount keeps
 * happening after the banner is done, so that count keeps coming.
 *
 * It also reports whether the banner is on screen (setHomepageBannerShowing):
 * PaidStudentHome only preloads the other homepage while it is.
 *
 * @param {Object} params
 * @param {boolean} params.isNewHomepage - the version the student is looking at
 * @returns {{ isVisible: boolean, dismiss: () => void }}
 */
const useHomepageUpgradeBanner = ({ isNewHomepage }) => {
  const [bannerState, setBannerState] = useState(readBannerState);
  const [isClosedThisVisit, setIsClosedThisVisit] = useState(false);

  useEffect(() => {
    fireHomepageGtm("homepage_version_viewed", {
      event_label1: isNewHomepage ? HOMEPAGE_VERSION.NEW : HOMEPAGE_VERSION.OLD,
    });
    if (!bannerState.firstSeenAt) {
      const next = { ...bannerState, firstSeenAt: Date.now() };
      writeBannerState(next);
      setBannerState(next);
    }
    // Once per visit to the homepage, not on every version switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    const next = { ...bannerState, dismissCount: bannerState.dismissCount + 1 };
    writeBannerState(next);
    setBannerState(next);
    setIsClosedThisVisit(true);
    fireHomepageGtm("homepage_banner_dismissed", {
      event_label1: String(next.dismissCount),
    });
  };

  const isVisible =
    !isClosedThisVisit && isBannerActive(bannerState, Date.now());

  useEffect(() => {
    setHomepageBannerShowing(isVisible);
  }, [isVisible]);

  useEffect(() => () => setHomepageBannerShowing(false), []);

  return { isVisible, dismiss };
};

export default useHomepageUpgradeBanner;
